import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAi } from '../queryKeys';

import { downloadAiFile } from './downloadAiFile';
import { type AiFileResponse } from './types';

type DownloadAiFileQueryKey = ReturnType<typeof queryKeysAi.getDownloadAiFile>;

export const getDownloadAiFileQueryOptions = <TSelected = AiFileResponse>({
  blobInfoEncoded,
  options,
}: {
  blobInfoEncoded: string | undefined;
  options?: Omit<
    UseQueryOptions<AiFileResponse, unknown, TSelected, DownloadAiFileQueryKey>,
    'queryKey' | 'queryFn'
  >;
}) => {
  return queryOptions<
    AiFileResponse,
    unknown,
    TSelected,
    DownloadAiFileQueryKey
  >({
    enabled: Boolean(blobInfoEncoded),
    gcTime: ms('10m'),
    queryFn: async ({ signal }) => downloadAiFile(blobInfoEncoded, { signal }),
    queryKey: queryKeysAi.getDownloadAiFile(blobInfoEncoded),
    staleTime: ms('1h'),
    ...options,
  });
};

/**
 * Custom useQuery hook to download a file used for AI labeling or model evaluation from Azure Blob Storage.
 * @param blobInfoEncoded - The encoded blob info of the file to download.
 * @param options - Query options.
 * @returns The file.
 * @see https://fa-vestigas-dev.azurewebsites.net/docs#/Metadata/download_file_direct_download_file_direct__blob_info_encoded__get
 */
export const useQueryDownloadAiFile = (
  blobInfoEncoded: string | undefined,
  options?: Omit<UseQueryOptions<AiFileResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...getDownloadAiFileQueryOptions({
      blobInfoEncoded,
      options,
    }),
  });
};
